import * as React from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { sessionOrigin } from "@/api/auth";
import { useSession } from "@/auth/session";
import { useAccount, useActingAccountId, useRememberAccount } from "@/account/use-account";
import { socket, socketUrl } from "@/realtime/socket";
import { useCapabilities } from "./capabilities";
import { meetsRequirement, useOperator } from "@/auth/operator";
import { columns } from "@/domains/registry";
import { ContextColumn } from "./context-column";
import { domainById, domainsFor } from "./nav";
import { Rail } from "./rail";
import { TopBar } from "./topbar";

/**
 * Domain-wide hosts: components that must be alive exactly once while a
 * domain is open. They cannot live in the context column, which the shell
 * renders twice below `lg` (a hidden desktop copy and the sheet), so a
 * dialog opened from one copy would be torn down when the other closes.
 * Routing's host renders the in-place creation dialogs.
 */
const RoutingHost = React.lazy(() => import("@/domains/routing/create-in-place").then((m) => ({ default: m.CreateInPlaceHost })));
const domainHosts: Record<string, React.ComponentType> = { routing: RoutingHost };

/**
 * Option C: top bar, then [rail | context column | main]. Below `lg` the
 * column lives in a sheet and the rail moves to the bottom.
 */
export function AppShell() {
  const location = useLocation();
  const params = useParams<{ domain?: string; section?: string; accountId?: string; id?: string }>();
  const session = useSession((s) => s.session);
  const actingId = useActingAccountId();
  const account = useAccount(actingId);
  useRememberAccount(actingId, account.data?.name);
  const [columnOpen, setColumnOpen] = React.useState(false);
  const probed = useCapabilities();
  const operator = useOperator();
  // The portal owns its companies' users and its token reaches one
  // account only, so Accounts and Platform are not in its rail.
  const origin = sessionOrigin(session);
  const domains = React.useMemo(() => domainsFor(origin), [origin]);
  // Sections a platform module does not serve (404) or the operator's role
  // cannot use (superduper-only carriers, rates, configs; reseller-only
  // whitelabel) leave the column rather than showing an error.
  const hidden = React.useMemo(() => {
    const set = new Set(probed);
    for (const d of domains) for (const s of d.sections) if (!meetsRequirement(operator, s.requires)) set.add(s.id);
    return set;
  }, [probed, operator, domains]);

  // A portal session's blackhole lives on the cluster the mint named, not
  // on this origin; a change of origin is a different socket.
  const socketOrigin = session?.socketOrigin;
  React.useEffect(() => {
    socket.configure({
      url: socketUrl(socketOrigin),
      token: () => useSession.getState().session?.token,
      onAuthFailure: async () => {
        useSession.getState().logout();
        return false;
      },
    });
    socket.connect();
    return () => socket.disconnect();
  }, [socketOrigin]);

  const domain = domainById(params.domain ?? location.pathname.split("/")[1], domains);
  if (!domain) return <Navigate to={`/a/${session?.accountId}/pbx/users`} replace />;
  const section = params.section ?? domain.sections[0]?.id;
  const custom = columns[domain.id];
  const Host = domainHosts[domain.id];
  const column = (onNavigate?: () => void) =>
    custom ? (
      <React.Suspense fallback={null}>
        <custom.Component accountId={actingId} section={section} id={params.id} hidden={hidden} onNavigate={onNavigate} />
      </React.Suspense>
    ) : (
      <ContextColumn domain={domain} accountId={actingId} section={section} hidden={hidden} onNavigate={onNavigate} />
    );

  return (
    <div className="flex h-full flex-col">
      <TopBar onOpenColumn={() => setColumnOpen(true)} showAccount={domain.scope === "account" && origin !== "portal"} />
      <div className="flex min-h-0 flex-1">
        <div className="hidden lg:flex">
          <Rail accountId={actingId} current={domain.id} domains={domains} />
        </div>
        <div className={`hidden ${custom?.width ?? "w-52"} shrink-0 hairline-r lg:block`}>{column()}</div>
        <Sheet open={columnOpen} onOpenChange={setColumnOpen}>
          <SheetContent side="left" className="w-64 p-0">
            <SheetTitle className="sr-only">{domain.label} sections</SheetTitle>
            {column(() => setColumnOpen(false))}
          </SheetContent>
        </Sheet>
        <main className="min-w-0 flex-1 overflow-hidden bg-background">
          <Outlet context={{ domain, section, accountId: actingId, account: account.data }} />
        </main>
      </div>
      <div className="lg:hidden">
        <Rail accountId={actingId} current={domain.id} domains={domains} variant="bottom" />
      </div>
      {Host && (
        <React.Suspense fallback={null}>
          <Host />
        </React.Suspense>
      )}
    </div>
  );
}
